import React from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import type { InvoiceItem } from '../../../core/models/Invoice';
import { Plus, Trash2 } from 'lucide-react';

interface InvoiceItemsTableProps {
  items: InvoiceItem[];
  onChange: (items: InvoiceItem[]) => void;
  readOnly?: boolean;
}

export const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  items,
  onChange,
  readOnly = false,
}) => {
  const handleAddItem = () => {
    onChange([
      ...items,
      {
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      },
    ]);
  };

  const handleUpdateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    const updatedItems = [...items];
    const item = { ...updatedItems[index] } as InvoiceItem;

    if (field === 'quantity' || field === 'rate') {
      (item as any)[field] = value;
      item.amount = item.quantity * item.rate;
    } else {
      (item as any)[field] = value;
    }

    updatedItems[index] = item;
    onChange(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const tax = subtotal * 0.1; // 10% tax - should be configurable
  const total = subtotal + tax;

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Quantity</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Rate</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
              {!readOnly && <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr key={index}>
                <td className="px-4 py-3">
                  {readOnly ? (
                    <span className="text-sm">{item.description}</span>
                  ) : (
                    <Input
                      value={item.description}
                      onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                      placeholder="Item description"
                      className="w-full"
                    />
                  )}
                </td>
                <td className="px-4 py-3">
                  {readOnly ? (
                    <span className="text-sm">{item.quantity}</span>
                  ) : (
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20"
                      min="0"
                      step="0.01"
                    />
                  )}
                </td>
                <td className="px-4 py-3">
                  {readOnly ? (
                    <span className="text-sm">${item.rate.toFixed(2)}</span>
                  ) : (
                    <Input
                      type="number"
                      value={item.rate}
                      onChange={(e) => handleUpdateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                      className="w-24"
                      min="0"
                      step="0.01"
                    />
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium">${item.amount.toFixed(2)}</span>
                </td>
                {!readOnly && (
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <Trash2 className="h-4 w-4 text-error" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <Button variant="outline" onClick={handleAddItem} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Item
        </Button>
      )}

      <div className="flex justify-end">
        <div className="w-64 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Tax (10%):</span>
            <span className="font-medium">${tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-lg font-semibold border-t pt-2">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
